import { AdminNotificationItem } from 'features/notifications/data/notificationsApi'
import { useAdminNotifications } from 'features/notifications/hooks/useAdminNotifications'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BellIcon } from '../Icons'

import { NotificationToastStack } from 'features/notifications/presentation/components/NotificationToastStack'

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffInSec = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000))
  if (diffInSec < 60) return 'Just now'
  const diffInMin = Math.floor(diffInSec / 60)
  if (diffInMin < 60) return `${diffInMin}m ago`
  const diffInHours = Math.floor(diffInMin / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`
  const diffInDays = Math.floor(diffInHours / 24)
  return `${diffInDays}d ago`
}

export const NotificationDropdown = () => {
  const navigate = useNavigate()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const notificationsRef = useRef<HTMLDivElement>(null)

  const { notifications, toastStack, unreadCount, markAsRead, markAllAsRead, dismissToast, dismissAllToasts } =
    useAdminNotifications()

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const handleNotificationClick = (n: AdminNotificationItem) => {
    if (!n.read_at) {
      markAsRead(n.id)
    }

    setNotificationsOpen(false)

    if (n.action_url) {
      navigate(n.action_url)
    } else {
      navigate('/notifications')
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order_status':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/10 text-blue-500 flex-shrink-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
          </div>
        )
      case 'kyc_status':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-status-idle/10 text-status-idle flex-shrink-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
        )
      case 'payment_status':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-status-active/10 text-status-active flex-shrink-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        )
      case 'grievance':
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-danger/10 text-danger flex-shrink-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        )
      default:
        return (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        )
    }
  }

  return (
    <div className="relative" ref={notificationsRef}>
      <button
        onClick={() => setNotificationsOpen(!notificationsOpen)}
        className="relative rounded-full p-2 text-text-secondary hover:bg-bg hover:text-text-primary transition-colors focus:outline-none"
        title="Notifications"
      >
        <BellIcon size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-slate-950 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown Panel */}
      {notificationsOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 origin-top-right rounded-card border border-border bg-surface p-2 shadow-card ring-1 ring-black ring-opacity-5 animate-scaleUp z-30 text-left">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
            <h3 className="text-xs font-extrabold uppercase tracking-wider text-text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-[11px] font-bold text-primary hover:underline">
                Mark all as read
              </button>
            )}
          </div>

          {/* Scrollable list */}
          <div className="max-h-80 overflow-y-auto divide-y divide-border/40 py-1">
            {notifications.length > 0 ? (
              notifications.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`flex gap-3 p-3 hover:bg-bg/60 cursor-pointer transition-colors duration-150 relative ${
                    !n.read_at ? 'bg-bg/30' : ''
                  }`}
                >
                  {/* Left category icon */}
                  {getNotificationIcon(n.type)}

                  {/* Content block */}
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex justify-between items-start gap-1">
                      <p
                        className={`text-xs truncate font-bold ${
                          !n.read_at ? 'text-text-primary' : 'text-text-secondary'
                        }`}
                      >
                        {n.title}
                      </p>
                      <span className="text-[9px] font-medium text-text-secondary whitespace-nowrap pt-0.5">
                        {formatRelativeTime(n.created_at)}
                      </span>
                    </div>
                    <p className="text-[11px] text-text-secondary leading-normal line-clamp-2">{n.message}</p>
                  </div>

                  {/* Unread indicator dot */}
                  {!n.read_at && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-primary" />
                  )}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-xs text-text-secondary italic">No notifications to display</div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border pt-2 pb-1 text-center">
            <button
              onClick={() => {
                setNotificationsOpen(false)
                navigate('/notifications')
              }}
              className="text-xs font-bold text-text-secondary hover:text-text-primary transition-colors inline-block"
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}

      {/* Floating Modern Stacked Toast Popups */}
      <NotificationToastStack notifications={toastStack} onDismiss={dismissToast} onDismissAll={dismissAllToasts} />
    </div>
  )
}
